import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'Luxury Home Sales',
    description:
      'Strategic listing, precision pricing, and elevated positioning designed to maximize your property value and reach qualified buyers across Lake Tahoe and Sierra Nevada.',
    image:
      'https://public.readdy.ai/ai/img_res/cf25b127f46dbc48fd69765fe6bf3b63.jpg',
    icon: 'ri-home-4-line',
    label: 'Maximize Value',
    stat: '500+ Homes Sold',
  },
  {
    title: 'Buyer Representation',
    description:
      'Exclusive access to off-market listings with expert negotiation and intimate knowledge of the Lake Tahoe alpine market — tailored to your lifestyle and investment goals.',
    image:
      'https://readdy.ai/api/search-image?query=elegant%20affluent%20couple%20standing%20on%20private%20terrace%20of%20breathtaking%20lakefront%20estate%20overlooking%20crystal%20clear%20deep%20blue%20Lake%20Tahoe%20California%20golden%20sunset%20warm%20glow%20aspirational%20luxury%20lifestyle%20photography%20Sierra%20Nevada%20mountains%20photorealistic%20premium&width=800&height=960&seq=svc-buyer-2026&orientation=portrait',
    icon: 'ri-search-eye-line',
    label: 'Find Your Home',
    stat: 'Exclusive Access',
  },
  {
    title: 'Vacation & Second Homes',
    description:
      "Specialized guidance for second-home buyers navigating Tahoe's unique seasonal market dynamics — from rental regulations to long-term appreciation strategy.",
    image:
      'https://readdy.ai/api/search-image?query=stunning%20luxury%20alpine%20ski%20chalet%20exterior%20twilight%20evening%20warm%20amber%20light%20glowing%20through%20massive%20windows%20pristine%20white%20snow%20snowfall%20pine%20trees%20Lake%20Tahoe%20California%20resort%20community%20high%20end%20real%20estate%20photography%20photorealistic%20cinematic%20atmosphere&width=800&height=960&seq=svc-vacation-2026&orientation=portrait',
    icon: 'ri-sun-line',
    label: 'Alpine Lifestyle',
    stat: 'Resort Specialists',
  },
  {
    title: 'Investment Properties',
    description:
      'Data-driven investment advisory on high-performing Lake Tahoe and Sierra Nevada real estate opportunities — identifying premier assets with strong long-term upside.',
    image:
      'https://readdy.ai/api/search-image?query=aerial%20drone%20perspective%20wide%20angle%20luxury%20lakefront%20private%20compound%20Lake%20Tahoe%20California%20deep%20cobalt%20blue%20water%20Sierra%20Nevada%20mountain%20peaks%20pine%20forest%20premium%20investment%20real%20estate%20portfolio%20private%20docks%20boathouse%20properties%20photorealistic%20cinematic&width=800&height=960&seq=svc-invest-2026&orientation=portrait',
    icon: 'ri-funds-line',
    label: 'Grow Wealth',
    stat: '$500M+ Transacted',
  },
];

export default function ServicesOverview() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = containerRef.current?.querySelectorAll('.service-card');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.05 }
    );
    cards.forEach((card, i) => {
      const el = card as HTMLElement;
      el.style.opacity = '0';
      el.style.transform = 'translateY(60px)';
      el.style.transition = `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 bg-warm-white pt-32 pb-28 px-4 md:px-8 rounded-t-[2.5rem] -mt-20">
      {/* Subtle texture dots */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #2A2A2A 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-20">
          <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">What We Offer</p>
          <h2 className="font-serif text-5xl md:text-7xl text-charcoal font-light">
            Real Estate <em className="italic text-gold/90">Services</em>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="w-16 h-px bg-gold/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/60" />
            <div className="w-16 h-px bg-gold/40" />
          </div>
          <p className="text-stone text-sm max-w-lg mx-auto mt-6 leading-relaxed">
            Full-spectrum luxury real estate representation across Lake Tahoe and the Sierra Nevada — from first showing to final close.
          </p>
        </div>

        {/* Service cards */}
        <div ref={containerRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service) => (
            <div
              key={service.title}
              className="service-card group bg-white rounded-2xl overflow-hidden hover:translate-y-[-4px] transition-all duration-500 cursor-pointer"
              style={{ boxShadow: '0 2px 20px rgba(42,42,42,0.07), 0 0 0 1px rgba(240,235,224,0.8)' }}
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent" />
                {/* Stat badge top right */}
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/40 backdrop-blur-sm rounded-full border border-white/15">
                  <span className="text-white text-[9px] tracking-[0.2em] uppercase">{service.stat}</span>
                </div>
                {/* Bottom label + icon */}
                <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                  <span className="text-[9px] tracking-[0.35em] uppercase text-gold/90">{service.label}</span>
                  <div className="w-8 h-8 flex items-center justify-center bg-gold rounded-lg">
                    <i className={`${service.icon} text-white text-sm`} />
                  </div>
                </div>
              </div>
              {/* Content */}
              <div className="p-6">
                <h3 className="font-serif text-xl text-charcoal font-medium mb-3">{service.title}</h3>
                <p className="text-stone text-sm leading-relaxed">{service.description}</p>
                <div className="mt-5 pt-5 border-t border-cream flex items-center gap-2 text-gold text-xs tracking-widest uppercase font-medium">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link
            to="/services"
            className="inline-block whitespace-nowrap px-10 py-4 border border-charcoal/30 text-charcoal text-xs tracking-[0.25em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal hover:text-warm-white transition-all duration-300"
          >
            Explore All Real Estate Services
          </Link>
        </div>
      </div>
    </section>
  );
}
